import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatEUR } from "@/lib/utils";
import { fmtData, useUltimaRichiesta } from "@/lib/listaColonne";
import { useConfirm } from "@/components/ConfirmProvider";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BarChart3, Columns3, Contact, List, Mail, Phone, Plus, Settings, SlidersHorizontal } from "lucide-react";
import {
  STATI_OPP, ContatoreContatto, useCrmMe, gestioneQualsiasi, errore, useAziendaCrm, CampoAzienda,
  TitoloCrm, PillLead, Segnalazione, pipelineScelta, ricordaPipeline, Pallino, TIPI_SOGGETTO, fmtTelefono,
} from "./crmComuni";
import VistaRigheCrm from "./VistaRigheCrm";
import FiltriCrm, { FILTRI_VUOTI, queryFiltri, contaFiltri, leggiCard, salvaCard } from "./FiltriCrm";
import NuovaOpportunita from "./NuovaOpportunita";
import Recapiti from "./Recapiti";
import RigaIcone from "./RigaIcone";
import AzioniBlocco from "./AzioniBlocco";

// Sprint CRM-2A.1 — niente «valore»: prezzo di vendita del veicolo agganciato (calcolato) o descritto; «—» se manca
export const PrezzoVendita = ({ o }) => <span className="font-mono" data-testid={`crm-prezzo-vendita-${o.id}`}>{formatEUR(o.prezzo_vendita_mostrato)}</span>;

// Sprint CRM-2A — etichette di lavoro: «Libera» (senza titolare) e «Ferma da N giorni» (oltre il tempo massimo)
export const Ferma = ({ o }) => (o.ferma_da != null ? <Segnalazione testid={`crm-ferma-${o.id}`}>Ferma da {o.ferma_da} giorni</Segnalazione> : null);
export const Libera = ({ o }) => (o.libera
  ? <span className="inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold bg-emerald-100 text-emerald-800" data-testid={`crm-libera-${o.id}`}
    title={o.rilasciata_da_giorni != null ? `nessuna attività da ${o.rilasciata_da_giorni} giorni` : undefined}>
    {o.rilasciata_da_giorni != null ? "Libera · rilasciata per inattività" : "Libera"}</span>
  : null);

export async function prendiInCarico(o, dopo) {
  try { await api.post(`/crm/opportunita/${o.id}/prendi-in-carico`); toast.success("Presa in carico"); }
  catch (e) { toast.error(errore(e)); }
  dopo();
}


const lsFiltri = (pid) => `crm-filtri-${pid}`;
const LS_PANNELLO = "crm-filtri-aperti";
const leggiFiltri = (pid) => { try { return { ...FILTRI_VUOTI, ...JSON.parse(localStorage.getItem(lsFiltri(pid)) || "{}") }; } catch { return FILTRI_VUOTI; } };
// Sprint CRM-5 — ultima vista scelta (colonne o elenco), per pipeline
const lsVista = (pid) => `crm-vista-${pid}`;
const leggiVista = (pid) => { try { return localStorage.getItem(lsVista(pid)) || "colonne"; } catch { return "colonne"; } };
const leggiPannello = () => { try { return localStorage.getItem(LS_PANNELLO) !== "0"; } catch { return true; } };

// La pill conta ciò che si vede: a colonne le schede nelle colonne, a righe tutte quelle che rispettano i filtri
export const leadMostrate = (dati, vista) => {
  if (!dati) return null;
  return vista === "colonne" ? (dati.colonne || []).reduce((n, c) => n + c.items.length, 0) : dati.total;
};

export default function CrmOpportunita() {
  const crmMe = useCrmMe();
  const [cid, setCid, aziende] = useAziendaCrm(crmMe);
  const [sp, setSp] = useSearchParams();
  const [pipelines, setPipelines] = useState([]);
  const pid = pipelineScelta(cid, pipelines, sp.get("pipeline"));
  const vista = sp.get("vista") || (pid ? leggiVista(pid) : "colonne");
  const [f, setFState] = useState(FILTRI_VUOTI);
  const [opzioni, setOpzioni] = useState(null);
  const [dati, setDati] = useState(null);
  const [sort, setSort] = useState({ by: "updated_at", order: "desc" });
  const [nuova, setNuova] = useState(false);
  const [pannello, setPannello] = useState(leggiPannello);
  const [card, setCardState] = useState(leggiCard);
  const setCard = (c) => { setCardState(c); salvaCard(c); };
  const ultima = useUltimaRichiesta();
  const setParam = (k, v) => {
    if (k === "vista" && pid) { try { localStorage.setItem(lsVista(pid), v); } catch {} }
    const n = new URLSearchParams(sp); n.set(k, v); setSp(n, { replace: true });
  };
  const setF = (x) => { setFState(x); try { localStorage.setItem(lsFiltri(pid), JSON.stringify(x)); } catch {} };
  const apriChiudi = () => { setPannello(!pannello); try { localStorage.setItem(LS_PANNELLO, pannello ? "0" : "1"); } catch {} };

  useEffect(() => {
    if (!cid) return;
    api.get(`/crm/pipeline?company_id=${cid}`).then((r) => setPipelines(r.data)).catch(() => setPipelines([]));
  }, [cid]);
  useEffect(() => {
    if (!pid) return;
    ricordaPipeline(cid, pid);
    // Sprint CRM-4 — numero della dashboard: i suoi filtri diventano i filtri della pipeline, poi si tolgono dall'indirizzo
    const daDashboard = sp.get("filtri");
    if (daDashboard) {
      try { const x = { ...FILTRI_VUOTI, ...JSON.parse(daDashboard) }; setFState(x); localStorage.setItem(lsFiltri(pid), JSON.stringify(x)); }
      catch { setFState(leggiFiltri(pid)); }
      const n = new URLSearchParams(sp); n.delete("filtri"); setSp(n, { replace: true });
    } else setFState(leggiFiltri(pid));
    api.get(`/crm/opportunita/opzioni?pipeline_id=${pid}`).then((r) => setOpzioni(r.data)).catch(() => setOpzioni(null));
  }, [pid, cid]); // eslint-disable-line react-hooks/exhaustive-deps

  const carica = useCallback(() => {
    if (!pid) return;
    const valida = ultima();
    const p = queryFiltri(f, vista);
    p.set("pipeline_id", pid);
    if (vista === "righe") { p.set("sort_by", sort.by); p.set("sort_order", sort.order); p.set("page_size", "200"); }
    api.get(`/crm/opportunita${vista === "colonne" ? "/colonne" : ""}?${p}`)
      .then((r) => { if (valida()) setDati(r.data); }).catch((e) => toast.error(errore(e)));
  }, [pid, f, vista, sort, ultima]);
  useEffect(() => { const t = setTimeout(carica, 250); return () => clearTimeout(t); }, [carica]);

  const pipeline = pipelines.find((p) => p.id === pid);
  const nFiltri = contaFiltri(f, vista);
  return (
    <div className="space-y-4" data-testid="crm-page">
      <div>
        {/* Sprint CRM-5 — riga ristretta: etichette sopra i campi, pulsanti a capo se manca spazio, mai sovrapposti */}
        <div className="flex items-end gap-x-3 gap-y-2 flex-wrap" data-testid="crm-intestazione">
          <div className="flex items-center gap-2 pb-1.5"><TitoloCrm />{!!pipelines.length && <PillLead n={leadMostrate(dati, vista)} />}</div>
          <CampoAzienda aziende={aziende} cid={cid} onScegli={(id) => { setCid(id); setPipelines([]); setDati(null); }} />
          {!!pipelines.length && <div className="flex flex-col gap-0.5"><Label className="text-[11px] text-slate-500" data-testid="crm-etichetta-pipeline">Pipeline</Label>
            <Select value={pid} onValueChange={(v) => setParam("pipeline", v)}>
              <SelectTrigger className="w-56" data-testid="crm-pipeline-select"><SelectValue /></SelectTrigger>
              <SelectContent>{pipelines.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent>
            </Select></div>}
          <div className="ml-auto flex flex-wrap justify-end gap-2" data-testid="crm-intestazione-pulsanti">
            <Button variant="outline" asChild data-testid="crm-dashboard-btn"><Link to="/crm/dashboard"><BarChart3 className="h-4 w-4 mr-1" /> Dashboard</Link></Button>
            {!!pipelines.length && <Button variant={pannello ? "default" : "outline"} onClick={apriChiudi} title="Filtri" aria-label="Filtri" className="relative px-3" data-testid="crm-filtri-btn">
              <SlidersHorizontal className="h-4 w-4" />
              {!!nFiltri && <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] leading-[18px]" data-testid="crm-filtri-n">{nFiltri}</span>}</Button>}
            <Button variant="outline" asChild title="Contatti" aria-label="Contatti" className="px-3" data-testid="crm-contatti-btn"><Link to="/crm/contatti"><Contact className="h-4 w-4" /></Link></Button>
            {gestioneQualsiasi(crmMe) && (
              <Button variant="outline" asChild title="Impostazioni" data-testid="crm-impostazioni-btn">
                <Link to="/crm/impostazioni"><Settings className="h-4 w-4 mr-1" /> Impost.</Link>
              </Button>
            )}
            {pipeline && <Button onClick={() => setNuova(true)} className="bg-[#0F4C81] hover:bg-[#0c3d68]" data-testid="crm-nuova-btn">
              <Plus className="h-4 w-4 mr-1" /> Nuova opportunità</Button>}
          </div>
        </div>
        {pipeline?.descrizione && <p className="text-sm text-slate-500 mt-1" data-testid="crm-pipeline-descrizione">{pipeline.descrizione}</p>}
      </div>

      {!pipelines.length ? (
        <Card className="p-8 text-center text-slate-500" data-testid="crm-nessuna-pipeline">Nessuna pipeline accessibile.</Card>
      ) : (
        <Card className="p-4 space-y-3">
          <div className="flex gap-3 items-start">
            <div className="flex-1 min-w-0"><FiltriCrm f={f} setF={setF} opzioni={opzioni} vista={vista} aperto={pannello} card={card} setCard={setCard} /></div>
            <div className="flex gap-1">
              <Button size="sm" variant={vista === "colonne" ? "default" : "outline"} onClick={() => setParam("vista", "colonne")} data-testid="crm-vista-colonne"><Columns3 className="h-4 w-4" /></Button>
              <Button size="sm" variant={vista === "righe" ? "default" : "outline"} onClick={() => setParam("vista", "righe")} data-testid="crm-vista-righe"><List className="h-4 w-4" /></Button>
            </div>
          </div>
          {dati && vista === "colonne" && !!dati.in_fasi_archiviate && (
            <div className="text-sm rounded border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900" data-testid="crm-archiviate-avviso">
              {dati.in_fasi_archiviate} opportunità sono in fasi archiviate:{" "}
              <button className="text-blue-600 hover:underline" onClick={() => setParam("vista", "righe")} data-testid="crm-archiviate-righe">vedile nella vista a righe</button>
            </div>
          )}
          {dati && vista === "colonne" && dati.colonne && <VistaColonne dati={dati} pipeline={pipeline} onCambio={carica} card={card} etichette={opzioni?.etichette || []}
            onTutteVinte={() => { setF({ ...f, stati: ["vinta"] }); setParam("vista", "righe"); }} />}
          {dati && vista === "righe" && dati.items && <AzioniBlocco items={dati.items} pipelineId={pid} crmMe={crmMe} onFatto={carica}>
            {(sel) => <VistaRigheCrm key={pid} pid={pid} items={dati.items} sort={sort} setSort={setSort} sel={sel} celle={{ Ferma, Libera, PrezzoVendita }} />}</AzioniBlocco>}
        </Card>
      )}
      {nuova && pipeline && <NuovaOpportunita pipeline={pipeline} onChiudi={() => setNuova(false)} />}
    </div>
  );
}

function VistaColonne({ dati, pipeline, onCambio, onTutteVinte, card, etichette }) {
  const confirm = useConfirm();
  const [trascinata, setTrascinata] = useState(null);
  const sposta = async (fase) => {
    const o = trascinata; setTrascinata(null);
    if (!o || o.fase_id === fase.id) return;
    if (fase.tipo === "chiusura_collegata") {
      toast.error(pipeline?.tipo === "acquisizione" ? "Si arriva qui collegando il veicolo caricato" : "Si arriva qui collegando una pratica");
      return;
    }
    const versoChiusura = fase.tipo !== "normale";
    const daChiusura = o.fase?.tipo !== "normale";
    if (versoChiusura !== daChiusura) {
      const ok = await confirm({ variant: "approve", title: versoChiusura ? "Segna come vinta" : "Rimetti in corso",
        message: versoChiusura ? `Spostandola in «${fase.nome}» l'opportunità diventa vinta. Confermi?`
          : `Spostandola in «${fase.nome}» l'opportunità torna in corso. Confermi?`, confirmLabel: "SI, sposta" });
      if (!ok) return;
    }
    try {
      await api.post(`/crm/opportunita/${o.id}/fase`, { fase_id: fase.id, fase_partenza: o.fase_id });
      onCambio();
    } catch (e) { toast.error(errore(e)); onCambio(); }
  };
  return (
    <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory lg:snap-none" data-testid="crm-colonne">
      {dati.colonne.map(({ fase, items, solo_ultimi_30_giorni: ult }) => (
        <div key={fase.id} className="w-[82vw] sm:w-80 lg:w-72 shrink-0 snap-start rounded-md bg-slate-50 border border-slate-200"
          onDragOver={(e) => e.preventDefault()} onDrop={() => sposta(fase)} data-testid={`crm-colonna-${fase.codice}`}>
          <div className="px-3 py-2 border-b border-slate-200 flex items-center gap-2" style={{ borderTop: `6px solid ${fase.colore || "#64748b"}` }}
            data-testid={`crm-colonna-banda-${fase.codice}`}>
            <span className="font-semibold text-sm text-slate-800">{fase.nome}</span>
            <span className="text-xs text-slate-500" data-testid={`crm-colonna-n-${fase.codice}`}>{items.length}</span>
          </div>
          <div className="p-2 space-y-2 min-h-[80px]">
            {items.map((o) => <SchedaColonna key={o.id} o={o} onDrag={() => setTrascinata(o)} onCambio={onCambio} card={card} etichette={etichette} />)}
            {ult && <button onClick={onTutteVinte} className="text-xs text-blue-600 hover:underline" data-testid={`crm-colonna-tutte-${fase.codice}`}>
              Ultimi 30 giorni · vedi tutte le vinte</button>}
          </div>
        </div>
      ))}
    </div>
  );
}

// Sprint CRM-2A.1 — righe accese in «Personalizza card», sempre nello stesso ordine; dato mancante = «—»
const primo = (lista, f = (x) => x) => (lista?.length ? `${f(lista[0])}${lista.length > 1 ? ` +${lista.length - 1}` : ""}` : "—");
const RigaCard = ({ k, o, children, destra }) => (
  <div className="flex items-center justify-between gap-2 min-h-[1.5rem]" data-testid={`crm-scheda-${k}-${o.id}`}>
    <span className="truncate">{children}</span>{destra}</div>
);

function SchedaColonna({ o, onDrag, onCambio, card, etichette }) {
  const nav = useNavigate();
  const chiusa = o.stato === "persa" || o.stato === "abbandonata";  // non si trascina: si riapre dallo stato
  const bordo = o.ferma_da != null ? "border-amber-400 border-2" : "border-slate-200";
  const k = o.contatto;
  const giorni = o.giorni_in_fase == null ? "—" : `${o.giorni_in_fase} giorni nella fase`;
  return (
    <div draggable={!chiusa} onDragStart={chiusa ? undefined : onDrag} onClick={() => nav(`/crm/opportunita/${o.id}`)}
      className={`bg-white rounded border ${bordo} p-2 text-sm cursor-pointer hover:border-sky-300 transition-colors space-y-0.5 ${chiusa ? "opacity-55" : ""}`}
      data-testid={`crm-scheda-${o.id}`}>
      {chiusa && <div className="text-[11px] font-semibold uppercase text-rose-700" data-testid={`crm-scheda-etichetta-${o.id}`}>{STATI_OPP[o.stato]}</div>}
      {o.libera && <div className="flex flex-wrap gap-1"><Libera o={o} /></div>}
      <div className="flex items-start justify-between gap-2">
        <span className="font-medium text-slate-900 truncate" data-testid={`crm-scheda-descrizione-${o.id}`}>{o.descrizione}</span>
        <Pallino id={o.titolare_id} nome={o.titolare_nome} testid={`crm-pallino-${o.id}`} />
      </div>
      {card.soggetto && <RigaCard k="soggetto" o={o} destra={<span className="text-xs text-slate-500">{TIPI_SOGGETTO[k?.tipo_soggetto] || "—"}</span>}>
        {k?.nome || "—"}</RigaCard>}
      {card.telefono && <RigaCard k="telefono" o={o} destra={<Recapiti contatto={k} opportunita={[o]} prefix={`crm-scheda-recapiti-${o.id}`}
        onRegistrata={onCambio} tipi={["chiamata", "whatsapp"]} />}><span className="inline-flex items-center gap-1"><Phone className="h-3 w-3 text-slate-400 shrink-0" />
        <span className="font-mono">{primo(k?.telefoni, fmtTelefono)}</span></span></RigaCard>}
      {card.email && <RigaCard k="email" o={o}><span className="inline-flex items-center gap-1"><Mail className="h-3 w-3 text-slate-400 shrink-0" />{primo(k?.email)}</span></RigaCard>}
      {card.contatore && <RigaCard k="conteggi" o={o}><ContatoreContatto conteggi={k?.conteggi} testid={`crm-scheda-contatore-${o.id}`} /></RigaCard>}
      {card.prezzo_vendita && <RigaCard k="pv" o={o}><span className="text-xs text-slate-500">Prezzo di vendita </span><PrezzoVendita o={o} /></RigaCard>}
      {card.prezzo_web && <RigaCard k="pw" o={o}><span className="text-xs text-slate-500">Prezzo web </span>
        <span className="font-mono">{formatEUR(o.prezzo_web_mostrato)}</span></RigaCard>}
      {card.date && <RigaCard k="date" o={o}><span className="text-[11px] text-slate-500">Creata: {fmtData(o.created_at)} - Aggiornata: {fmtData(o.updated_at)}</span></RigaCard>}
      {card.giorni && <RigaCard k="giorni" o={o} destra={<Ferma o={o} />}><span className="text-[11px] text-slate-400">{giorni}</span></RigaCard>}
      {card.fonte && <RigaCard k="fonte" o={o}><span className="text-xs text-slate-500">Fonte: {o.fonte_nome || "—"}</span></RigaCard>}
      {card.prossima && <RigaCard k="prossima" o={o}><span className="text-xs">Prossima attività: {o.prossima_attivita
        ? `${fmtData(o.prossima_attivita.inizio)} – ${o.prossima_attivita.titolo}` : "—"}</span></RigaCard>}
      {card.icone && <RigaIcone o={o} etichette={etichette} onCambio={onCambio} />}
      {o.puo_prendere && <div className="flex justify-end"><Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" data-testid={`crm-prendi-${o.id}`}
        onClick={(e) => { e.stopPropagation(); prendiInCarico(o, onCambio); }}>Prendi in carico</Button></div>}
    </div>
  );
}
